import { usePatientSubscription } from '@ctw/shared/api/subscriptions/subscriptions';
import { Button } from '@ctw/shared/components/button';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { type ClassName, twx } from '@ctw/shared/utils/tailwind';
import type { ReactNode } from 'react';
import { useRequestRecordsModal } from './request-records-modal';
import { usePatientHistory } from './use-patient-history';

type RequestRecordsButtonProps = {
  className?: ClassName;
  children?: string | ReactNode;
};

export const RequestRecordsButton = ({
  className,
  children = 'Request records',
}: RequestRecordsButtonProps) => {
  const openRequestRecordsModal = useRequestRecordsModal();
  const {
    isLoading,
    details: { inProgress, completed },
    openHistoryRequestDrawer,
  } = usePatientHistory();
  const patientSubscription = usePatientSubscription();
  const shouldAlwaysAllowRequesting = useFeatureFlag('ctw-always-allow-requesting-records');
  const hasInProgressJobs = Boolean(inProgress);

  return (
    <Button
      type="button"
      variant="link"
      // Add line-height (leading) to increase the clickable area.
      className={twx('text-sm disabled:text-primary-text disabled:opacity-50', className)}
      telemetryInteractionName="request_records_open"
      disabled={isLoading || (!shouldAlwaysAllowRequesting && hasInProgressJobs)}
      onClick={() => {
        // Ideally we want to skip the modal when there's been zero requests
        // or when patient history needs to be requested manually (custom package).
        if (!completed || patientSubscription.data?.package?.requiresManualRequests) {
          void openHistoryRequestDrawer();
        } else {
          openRequestRecordsModal();
        }
      }}
    >
      {children}
    </Button>
  );
};
